import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { getAuthUrl } from "@/utils/redirectUtils";
import {
  ArrowRight,
  Sparkles,
  DollarSign,
  Eye,
  Target,
  Search,
  Filter,
  BarChart3,
  Layers,
  Rocket,
  X,
  Check,
  TrendingUp,
  Clock,
  Zap,
  MousePointerClick,
} from "lucide-react";

const KeywordOptimierungDE = () => {
  const problems = [
    {
      icon: <DollarSign className="h-8 w-8" />,
      title: "Budgeterschöpfung",
      description:
        "Ihr gesamtes Tagesbudget wird durch 1-2 Klicks auf teure Keywords aufgebraucht. Bis zum Vormittag werden Ihre Anzeigen nicht mehr geschaltet.",
    },
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Keine Sichtbarkeit",
      description:
        "Wenn das Budget vor Mittag aufgebraucht ist, ist Ihr Unternehmen die restlichen 14+ Stunden des Tages unsichtbar.",
    },
    {
      icon: <Target className="h-8 w-8" />,
      title: "Schlechte Anzeigenrelevanz",
      description:
        "Generische, teure Keywords ziehen Nutzer an, die nicht bereit sind zu konvertieren. Sie zahlen Premiumpreise für Traffic mit geringer Kaufabsicht.",
    },
  ];

  const pipelineSteps = [
    {
      icon: <Search className="h-6 w-6" />,
      title: "Budget & CPA-Analyse",
      description:
        "Upfixe analysiert Ihr Tagesbudget und Ihren Ziel-CPA, um genau zu bestimmen, welche Keywords Sie sich leisten können.",
    },
    {
      icon: <Filter className="h-6 w-6" />,
      title: "Bezahlbarkeitsfilter",
      description:
        "Keywords, die mehr als Ihren maximalen CPC kosten, werden sofort herausgefiltert. Kein Cent wird verschwendet.",
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: "Suchvolumen-Priorisierung",
      description:
        "Bezahlbare Keywords werden nach monatlichem Suchvolumen sortiert, damit Sie maximale Reichweite erzielen.",
    },
    {
      icon: <Layers className="h-6 w-6" />,
      title: "Thematische Gruppierung",
      description:
        "Keywords werden in eng verwandte Themengruppen organisiert, die Ihren Quality Score verbessern.",
    },
    {
      icon: <Rocket className="h-6 w-6" />,
      title: "Kampagnenfertige Anzeigengruppen",
      description:
        "Fertige Anzeigengruppen mit passenden Keywords, Anzeigentexten und Gebotsempfehlungen zum sofortigen Start.",
    },
  ];

  const withoutUpfixe = [
    { metric: "Aktive Keywords", value: "2 Keywords" },
    { metric: "Durchschnittlicher CPC", value: "42,50 \u20AC" },
    { metric: "Budgetstatus", value: "Erschöpft bis 10:15" },
    { metric: "Anzeigenrelevanz", value: "Niedrig" },
    { metric: "Tägliche Impressionen", value: "~120" },
  ];

  const withUpfixe = [
    { metric: "Aktive Keywords", value: "89+ Keywords" },
    { metric: "Durchschnittlicher CPC", value: "5,80 \u20AC" },
    { metric: "Budgetstatus", value: "Hält den ganzen Tag" },
    { metric: "Anzeigenrelevanz", value: "Hoch" },
    { metric: "Tägliche Impressionen", value: "~2.400+" },
  ];

  const stats = [
    {
      icon: <TrendingUp className="h-6 w-6 text-primary" />,
      value: "+340%",
      label: "Mehr aktive Keywords",
    },
    {
      icon: <DollarSign className="h-6 w-6 text-primary" />,
      value: "-62%",
      label: "Weniger verschwendete Ausgaben",
    },
    {
      icon: <Target className="h-6 w-6 text-primary" />,
      value: "+85%",
      label: "Höhere Anzeigenrelevanz",
    },
    {
      icon: <MousePointerClick className="h-6 w-6 text-primary" />,
      value: "3.2x",
      label: "Mehr tägliche Impressionen",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Keyword-Optimierung | Budgetbasierte Google Ads Keywords | Upfixe</title>
        <meta
          name="description"
          content="Verschwenden Sie kein Google Ads Budget mehr für unbezahlbare Keywords. Upfixe filtert Keywords nach CPA, priorisiert nach Suchvolumen und organisiert in thematische Anzeigengruppen."
        />
        <meta
          name="keywords"
          content="keyword optimierung, google ads keyword strategie, budgetbasierte keyword auswahl, google ads budget optimierung, anzeigengruppen organisation"
        />
        <link rel="canonical" href="https://upfixe.com/de/keyword-optimierung" />
        <meta
          property="og:title"
          content="Keyword-Optimierung | Budgetbasierte Google Ads Keywords | Upfixe"
        />
        <meta
          property="og:description"
          content="Verschwenden Sie kein Google Ads Budget mehr für unbezahlbare Keywords. Upfixe filtert Keywords nach CPA und organisiert in thematische Anzeigengruppen."
        />
      </Helmet>
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-32 pb-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background to-primary/5" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/3 rounded-full blur-3xl" />

          <div className="container mx-auto max-w-4xl relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge className="px-4 py-1.5 text-sm bg-primary/10 text-primary border-primary/20 mb-6">
                Budgetbasierte Keyword-Auswahl
              </Badge>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Hören Sie auf, Ihr Budget für{" "}
              <span className="text-primary">unbezahlbare Keywords</span> zu verschwenden
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-10 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Die meisten Werbetreibenden verbrennen ihr Google Ads Budget für Keywords, die sie sich
              nicht leisten können. Upfixe filtert automatisch nach Ihrem Budget, priorisiert nach
              Suchvolumen und erstellt kampagnenfertige Anzeigengruppen.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button
                size="lg"
                className="h-14 px-10 text-lg"
                onClick={() => (window.location.href = getAuthUrl())}
              >
                Kostenlose Kampagne starten <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="h-14 px-10 text-lg" asChild>
                <Link to="/pricing">Preise ansehen</Link>
              </Button>
            </motion.div>

            <motion.p
              className="text-sm text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              Keine Kreditkarte erforderlich. Ihre erste Kampagne ist kostenlos.
            </motion.p>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <Badge className="px-3 py-1 text-sm bg-red-100 text-red-700 border-red-200 mb-4">
                  Das Problem
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  Die meisten Werbetreibenden verbrennen ihr Budget bis 10 Uhr
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Der Standard-Ansatz bei Google Ads ist, auf wettbewerbsstarke Keywords zu bieten.
                  Aber bei einem Tagesbudget von 30-100 Euro reicht ein einziger Klick auf ein
                  40-Euro-Keyword, um Ihr Budget aufzubrauchen.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {problems.map((problem, index) => (
                <motion.div
                  key={problem.title}
                  className="rounded-xl border border-red-200 bg-red-50/50 p-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 text-red-600 mb-4">
                    {problem.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{problem.title}</h3>
                  <p className="text-muted-foreground">{problem.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 5-Step Pipeline */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  So funktioniert es
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  5-Schritte Keyword-Pipeline
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Von Ihrem Budget bis zu kampagnenfertigen Anzeigengruppen — vollautomatisch.
                </p>
              </motion.div>
            </div>

            <div className="space-y-6">
              {pipelineSteps.map((step, index) => (
                <motion.div
                  key={step.title}
                  className="flex items-start gap-6 bg-white rounded-xl border border-border p-6 shadow-sm"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">
                    {index + 1}
                  </div>
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    {step.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Schritt {index + 1}: {step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-24 px-4 bg-background">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  Direktvergleich
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  Der Unterschied ist wie Tag und Nacht
                </h2>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Ohne Upfixe */}
              <motion.div
                className="rounded-2xl border-2 border-red-200 overflow-hidden"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-red-50 px-6 py-4 border-b border-red-200">
                  <div className="flex items-center gap-2">
                    <X className="h-6 w-6 text-red-500" />
                    <h3 className="text-lg font-bold text-red-800">Ohne Upfixe</h3>
                  </div>
                  <p className="text-sm text-red-600 mt-1">Manuelle Keyword-Auswahl</p>
                </div>
                <div className="divide-y divide-red-100">
                  {withoutUpfixe.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="px-6 py-4 flex justify-between items-center"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.08 }}
                    >
                      <span className="text-sm text-muted-foreground">{item.metric}</span>
                      <span className="text-sm font-bold text-red-700">{item.value}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Mit Upfixe */}
              <motion.div
                className="rounded-2xl border-2 border-green-200 overflow-hidden shadow-lg shadow-green-100/50"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-green-50 px-6 py-4 border-b border-green-200">
                  <div className="flex items-center gap-2">
                    <Check className="h-6 w-6 text-green-600" />
                    <h3 className="text-lg font-bold text-green-800">Mit Upfixe</h3>
                  </div>
                  <p className="text-sm text-green-600 mt-1">Budgetbasierte Keyword-Optimierung</p>
                </div>
                <div className="divide-y divide-green-100">
                  {withUpfixe.map((item, index) => (
                    <motion.div
                      key={item.metric}
                      className="px-6 py-4 flex justify-between items-center"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.08 }}
                    >
                      <span className="text-sm text-muted-foreground">{item.metric}</span>
                      <span className="text-sm font-bold text-green-700">{item.value}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Results / Stats Section */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
              >
                <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
                  Ergebnisse
                </Badge>
                <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
                  Echte Ergebnisse, messbare Wirkung
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Durchschnittliche Verbesserungen unserer Nutzer nach dem Wechsel zur
                  budgetbasierten Keyword-Optimierung.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="bg-white rounded-xl border border-border p-6 text-center shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                    {stat.icon}
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                    {stat.value}
                  </div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />

          <div className="container mx-auto max-w-4xl relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Sparkles className="h-12 w-12 text-primary mx-auto mb-6" />
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Bereit, jeden Euro zählen zu lassen?
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
                Hören Sie auf zu raten, welche Keywords Sie verwenden sollen. Lassen Sie Upfixe
                automatisch die Keywords finden, die Ihr Budget verträgt, nach Suchvolumen
                priorisieren und in leistungsstarke Anzeigengruppen organisieren.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  size="lg"
                  className="h-14 px-10 text-lg"
                  onClick={() => (window.location.href = getAuthUrl())}
                >
                  Kostenlose Kampagne starten <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button variant="outline" size="lg" className="h-14 px-10 text-lg" asChild>
                  <Link to="/pricing">Preise ansehen</Link>
                </Button>
              </div>

              <p className="text-sm text-muted-foreground">
                Keine Kreditkarte erforderlich. Ihre erste Kampagne ist kostenlos.
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default KeywordOptimierungDE;
